import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
} from 'react-image-crop';
import { Button, Slider, Row, Col, Typography, Space, Upload, Divider, Alert } from 'antd';
import { RotateLeftOutlined, RotateRightOutlined, UploadOutlined, ZoomInOutlined, ZoomOutOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { canvasPreview } from './canvasPreview';
import { useDebounceEffect } from './useDebounceEffect';

import 'react-image-crop/dist/ReactCrop.css';

const { Text } = Typography;

// This is to demonstate how to get a blob object with a cropped image part
function toBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas toBlob returned null'));
        }
      }, 'image/jpeg', 0.95);
    } catch (error) {
      reject(error);
    }
  });
}

interface ImageCropperProps {
  initialImageUrl?: string;
  onImageCropped?: (croppedImageFile: File, width: number, height: number) => void;
  onCancel?: () => void;
  aspect?: number;
}

const ImageCropper: React.FC<ImageCropperProps> = ({ initialImageUrl, onImageCropped, onCancel, aspect }) => {
  const [imgSrc, setImgSrc] = useState(initialImageUrl);
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);

  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined); // Makes crop preview update between images.
      const reader = new FileReader();
      reader.addEventListener('load', () =>
        setImgSrc(reader.result?.toString() || ''),
      );
      reader.readAsDataURL(e.target.files[0]);
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    setCrop(centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        width,
        height,
      ),
      width,
      height,
    ));
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate,
        );
      }
    },
    100,
    [completedCrop, scale, rotate],
  );

  useEffect(() => {
    if (initialImageUrl) {
      setImgSrc(initialImageUrl);
    }
  }, [initialImageUrl]);

  const handleSave = async () => {
    console.log('Save clicked - starting image processing');

    if (!imgRef.current || !previewCanvasRef.current || !onImageCropped) {
      console.error('Missing required refs or callback', {
        hasImgRef: !!imgRef.current,
        hasCanvasRef: !!previewCanvasRef.current,
        hasCallback: !!onImageCropped
      });
      return;
    }

    // Check if crop is valid (has both width and height > 0)
    const isCropValid = completedCrop && completedCrop.width > 0 && completedCrop.height > 0;

    // If no valid crop, use the full displayed image size (in display coordinates)
    // IMPORTANT: crop coordinates must be in display pixels, not natural pixels
    const cropToUse: PixelCrop = isCropValid ? completedCrop : {
      x: 0,
      y: 0,
      width: imgRef.current.width,   // Display width, not natural width
      height: imgRef.current.height, // Display height, not natural height
      unit: 'px' as const,
    };

    // Maximum canvas dimension to prevent browser limits
    const MAX_DIMENSION = 4096;
    const naturalWidth = imgRef.current.naturalWidth;
    const naturalHeight = imgRef.current.naturalHeight;
    const maxDim = Math.max(naturalWidth, naturalHeight);
    const scaleFactor = maxDim > MAX_DIMENSION ? MAX_DIMENSION / maxDim : 1;

    console.log('Crop settings:', {
      cropToUse,
      scale,
      rotate,
      isCropValid,
      scaleFactor,
      imageSize: {
        displayWidth: imgRef.current.width,
        displayHeight: imgRef.current.height,
        naturalWidth: naturalWidth,
        naturalHeight: naturalHeight,
      }
    });

    // Generate the cropped image on the canvas
    await canvasPreview(
      imgRef.current,
      previewCanvasRef.current,
      cropToUse,
      scale,
      rotate,
    );

    const canvas = previewCanvasRef.current;

    console.log('Canvas preview generated. Canvas info:', {
      width: canvas.width,
      height: canvas.height,
      hasContext: !!canvas.getContext('2d')
    });

    // Check if canvas has any content
    if (canvas.width === 0 || canvas.height === 0) {
      console.error('Canvas has zero dimensions!');
      return;
    }

    try {
      const blob = await toBlob(canvas);

      if (blob) {
        console.log('Blob created:', { size: blob.size, type: blob.type });
        const croppedFile = new File([blob], "cropped-image.jpeg", { type: 'image/jpeg' });
        console.log('Calling onImageCropped callback...');
        onImageCropped(croppedFile, canvas.width, canvas.height);
      } else {
        console.error('Failed to create blob from canvas');
      }
    } catch (error) {
      console.error('Error creating blob:', error);
      if (error instanceof Error && error.message.includes('Tainted')) {
        console.error('CORS issue - image loaded from external domain without proper headers');
      }
    }
  };

  const handleRotateLeft = () => {
    setRotate((prev) => (prev - 90) % 360);
  };

  const handleRotateRight = () => {
    setRotate((prev) => (prev + 90) % 360);
  };

  const handleFileChange = (file: File) => {
    setCrop(undefined);
    setScale(1);
    setRotate(0);
    const reader = new FileReader();
    reader.addEventListener('load', () =>
      setImgSrc(reader.result?.toString() || ''),
    );
    reader.readAsDataURL(file);
    return false; // Prevent default upload behavior
  };

  // Calculate output dimensions
  const outputDimensions = useMemo(() => {
    if (!imgRef.current || !completedCrop) {
      return null;
    }

    const isCropValid = completedCrop.width > 0 && completedCrop.height > 0;
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    // Use crop if valid, otherwise use full image
    const cropWidth = isCropValid ? completedCrop.width * scaleX : imgRef.current.naturalWidth;
    const cropHeight = isCropValid ? completedCrop.height * scaleY : imgRef.current.naturalHeight;

    // Account for rotation
    const rotateRads = (rotate * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rotateRads));
    const cos = Math.abs(Math.cos(rotateRads));
    const rotatedWidth = cropWidth * cos + cropHeight * sin;
    const rotatedHeight = cropWidth * sin + cropHeight * cos;

    // Output dimensions (zoom/scale does NOT affect output)
    const outputWidth = Math.floor(rotatedWidth);
    const outputHeight = Math.floor(rotatedHeight);

    // Cap to max size
    const MAX_SIZE = 4096;
    let finalWidth = outputWidth;
    let finalHeight = outputHeight;
    if (outputWidth > MAX_SIZE || outputHeight > MAX_SIZE) {
      const scaleFactor = Math.min(MAX_SIZE / outputWidth, MAX_SIZE / outputHeight);
      finalWidth = Math.floor(outputWidth * scaleFactor);
      finalHeight = Math.floor(outputHeight * scaleFactor);
    }

    return {
      width: finalWidth,
      height: finalHeight,
      isCropped: isCropValid,
      isRotated: rotate !== 0
    };
  }, [completedCrop, rotate, imgRef.current]);

  return (
    <div style={{ padding: '16px' }}>
      <Space orientation="vertical" style={{ width: '100%' }} size="large">
        {/* File Upload */}
        <Row gutter={16} align="middle">
          <Col span={24}>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleFileChange}
            >
              <Button icon={<UploadOutlined />} block>
                Replace Image
              </Button>
            </Upload>
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0' }} />

        {/* Image Preview with Crop */}
        {imgSrc && (
          <div style={{ maxWidth: '100%', overflow: 'auto' }}>
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imgSrc}
                crossOrigin="anonymous"
                style={{
                  transform: `scale(${scale}) rotate(${rotate}deg)`,
                  maxWidth: '100%',
                  height: 'auto'
                }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          </div>
        )}

        {/* Controls */}
        {imgSrc && (
          <>
            <Divider style={{ margin: '8px 0' }}>Controls</Divider>

            {/* Rotation Controls */}
            <Row gutter={16} align="middle">
              <Col span={4}>
                <Text strong>Rotate:</Text>
              </Col>
              <Col span={8}>
                <Button icon={<RotateLeftOutlined />} onClick={handleRotateLeft} block>
                  Left 90°
                </Button>
              </Col>
              <Col span={8}>
                <Button icon={<RotateRightOutlined />} onClick={handleRotateRight} block>
                  Right 90°
                </Button>
              </Col>
              <Col span={4}>
                <Text type="secondary">{rotate}°</Text>
              </Col>
            </Row>

            {/* Scale/Zoom Controls */}
            <Row gutter={16} align="middle">
              <Col span={24}>
                <Text strong>Zoom (for viewing only):</Text>
              </Col>
            </Row>
            <Row gutter={16} align="middle">
              <Col span={20}>
                <Slider
                  min={0.5}
                  max={3}
                  step={0.1}
                  value={scale}
                  onChange={setScale}
                  marks={{ 0.5: '0.5x', 1: '1x', 2: '2x', 3: '3x' }}
                />
              </Col>
              <Col span={4}>
                <Text type="secondary">{scale.toFixed(1)}x</Text>
              </Col>
            </Row>

            {/* Output Dimensions Info */}
            {outputDimensions && (
              <Alert
                title={
                  <Space>
                    <InfoCircleOutlined />
                    <span>
                      <strong>Output:</strong> {outputDimensions.width} × {outputDimensions.height} pixels
                      {(outputDimensions.isCropped || outputDimensions.isRotated) && (
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          ({outputDimensions.isCropped && 'cropped'}{outputDimensions.isCropped && outputDimensions.isRotated && ', '}{outputDimensions.isRotated && 'rotated'})
                        </Text>
                      )}
                    </span>
                  </Space>
                }
                type="info"
                showIcon={false}
                style={{ marginBottom: 0 }}
              />
            )}

            {/* Preview Canvas (always rendered but hidden) */}
            <canvas
              ref={previewCanvasRef}
              style={{
                position: 'absolute',
                top: '-9999px',
                left: '-9999px'
              }}
            />

            <Divider style={{ margin: '8px 0' }} />

            {/* Action Buttons */}
            <Row gutter={16}>
              <Col span={12}>
                <Button onClick={onCancel} block>
                  Cancel
                </Button>
              </Col>
              <Col span={12}>
                <Button type="primary" onClick={handleSave} block>
                  Save Changes
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Space>
    </div>
  );
};

export default ImageCropper;